import { useState } from "react";
import { useTheme } from "./ThemeContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

const requirements = [
  { label: "At least 8 characters",       test: p => p.length >= 8 },
  { label: "One uppercase letter (A-Z)",   test: p => /[A-Z]/.test(p) },
  { label: "One lowercase letter (a-z)",   test: p => /[a-z]/.test(p) },
  { label: "One number (0-9)",             test: p => /\d/.test(p) },
  { label: "One special character",        test: p => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(p) },
];

export default function ResetPassword({ token, onGoToLogin }) {
  const { isDark } = useTheme();
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [error,     setError]     = useState("");
  const [done,      setDone]      = useState(false);
  const [email,     setEmail]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [pwFocused,    setPwFocused]   = useState(false);
  const [showPassword, setShowPw]      = useState(false);
  const [capsLock,     setCapsLock]    = useState(false);

  const passwordOk   = requirements.every(r => r.test(password));
  const confirmMatch = confirm.length > 0 && password === confirm;
  const confirmWrong = confirm.length > 0 && password !== confirm;

  const handle = async () => {
    if (!password || !confirm) { setError("Please fill in all fields"); return; }
    if (!passwordOk) { setError("Password does not meet the requirements"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }

    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newpass: password }),
      });
      const data = await res.json();
      if (data.success) {
        setEmail(data.email || "");
        setDone(true);
        window.history.replaceState({}, document.title, "/");
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (e) {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Fraunces:ital,wght@0,700;1,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; }
        .rp-btn:hover { background: color-mix(in srgb, var(--primary) 25%, transparent) !important; }
        .rp-btn { transition: background 0.15s; }
        .rp-input:focus { border-color: var(--border2) !important; outline: none; }
      `}</style>

      <div style={s.leftPanel}>
        <div style={s.brandMark}>K</div>
        <div style={s.brandName}>KourseKit</div>
        <div style={s.brandTagline}>Your academic life,<br />organized.</div>
        <div style={s.decorCircle1} />
        <div style={s.decorCircle2} />
      </div>

      <div style={s.rightPanel}>
        <div style={s.card}>
          {done ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" stroke="#2e7d32" fill="#f0faf0"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <h2 style={s.title}>Password Reset!</h2>
              <p style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.7, marginBottom: 28 }}>
                Your password has been updated. You can now log in with your new password.
              </p>
              <button className="rp-btn" onClick={() => onGoToLogin(email)} style={s.btn}>
                Go to Login
              </button>
            </div>
          ) : (
            <>
              <h2 style={s.title}>Set New Password</h2>
              <p style={s.subtitle}>Choose a strong password for your account</p>

              {error && <div style={s.errorBox}>{error}</div>}

              <label style={s.label}>New Password</label>
              <div style={{ position:"relative", marginBottom:16 }}>
                <input
                  className="rp-input"
                  style={{ ...s.input, marginBottom:0, paddingRight:42 }}
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }}
                  onFocus={() => setPwFocused(true)}
                  onKeyDown={e => { setCapsLock(e.getModifierState("CapsLock")); if (e.key === "Enter") handle(); }}
                  onKeyUp={e => setCapsLock(e.getModifierState("CapsLock"))}
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--text2)", padding:0, display:"flex", alignItems:"center" }}>
                  {showPassword
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>

              {(pwFocused || password.length > 0) && (
                <div style={s.reqBox}>
                  {requirements.map(r => {
                    const met = r.test(password);
                    return (
                      <div key={r.label} style={{ ...s.reqRow, color: met ? "#2e7d32" : "#7a8fa8" }}>
                        <span style={{ marginRight: 7, fontSize: 13 }}>{met ? "✓" : "○"}</span>
                        {r.label}
                      </div>
                    );
                  })}
                </div>
              )}

              <label style={s.label}>Confirm New Password</label>
              <div style={{ position:"relative", marginBottom:16 }}>
                <input
                  className="rp-input"
                  style={{
                    ...s.input, marginBottom:0,
                    borderColor: confirmWrong ? "#e74c3c" : confirmMatch ? "#2e7d32" : "#D4D4DC",
                  }}
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirm}
                  onChange={e => { setConfirm(e.target.value); setError(""); }}
                  onKeyDown={e => { setCapsLock(e.getModifierState("CapsLock")); if (e.key === "Enter") handle(); }}
                  onKeyUp={e => setCapsLock(e.getModifierState("CapsLock"))}
                />
              </div>
              {confirmWrong && (
                <p style={{ fontSize: 12, color: "#e74c3c", marginTop: -10, marginBottom: 12 }}>
                  Passwords don't match
                </p>
              )}
              {confirmMatch && (
                <p style={{ fontSize: 12, color: "#2e7d32", marginTop: -10, marginBottom: 12 }}>
                  Passwords match ✓
                </p>
              )}
              {capsLock && (
                <p style={{ fontSize: 12, color: isDark ? "#fcd34d" : "#b45309", background: isDark ? "#2a2000" : "#fffbeb", border: `1px solid ${isDark ? "#78500a" : "#fcd34d"}`, borderRadius: 8, padding: "6px 10px", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  ⇪ Caps Lock is on
                </p>
              )}

              <button
                className="rp-btn"
                onClick={handle}
                disabled={loading}
                style={{ ...s.btn, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Resetting…" : "Reset Password"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { display: "flex", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" },

  leftPanel: {
    width: 420, background: "#31487A", display: "flex",
    flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: 48, position: "relative", overflow: "hidden", flexShrink: 0,
  },
  brandMark: {
    width: 60, height: 60, borderRadius: 16, background: "#7B5EA7",
    color: "white", fontFamily: "'Fraunces', serif", fontWeight: 700,
    fontSize: 28, display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: 16, zIndex: 1,
  },
  brandName: {
    fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 32,
    color: "#ffffff", marginBottom: 16, zIndex: 1,
  },
  brandTagline: {
    fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 18,
    color: "rgba(255,255,255,0.6)", textAlign: "center", lineHeight: 1.6, zIndex: 1,
  },
  decorCircle1: {
    position: "absolute", width: 300, height: 300, borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.08)", bottom: -80, right: -80,
  },
  decorCircle2: {
    position: "absolute", width: 200, height: 200, borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.06)", top: -60, left: -60,
  },

  rightPanel: {
    flex: 1, background: "var(--bg)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 32,
  },
  card: {
    background: "var(--surface)", borderRadius: 20, padding: "40px 36px",
    boxShadow: "0 4px 24px rgba(30,58,110,0.1)", width: "100%", maxWidth: 420,
  },
  title: {
    fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 26,
    color: "var(--primary)", marginBottom: 6,
  },
  subtitle: { fontSize: 14, color: "var(--text2)", marginBottom: 24 },
  errorBox: {
    background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10,
    padding: "10px 14px", fontSize: 13, color: "var(--error)", marginBottom: 16,
  },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 6 },
  input: {
    width: "100%", padding: "11px 14px", border: "1px solid var(--border)",
    borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif",
    color: "var(--text)", background: "var(--surface2)", marginBottom: 16, display: "block",
    transition: "border-color 0.15s",
  },
  reqBox: {
    background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10,
    padding: "10px 14px", marginBottom: 16, marginTop: -8,
  },
  reqRow: {
    fontSize: 12, fontWeight: 500, padding: "2px 0", display: "flex", alignItems: "center",
  },
  btn: {
    width: "100%", padding: "13px", background: "color-mix(in srgb, var(--primary) 15%, transparent)", color: "var(--primary)",
    border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginTop: 4,
  },
};
